import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { getClients, createClient, updateClient, deleteClient } from '../lib/api';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  ActionButton,
  ActionGroup,
  AppDialog,
  StatusBadge,
} from '../components/app';
import { ClientForm } from '../components/clients';
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  Buildings,
  Envelope,
  Phone,
  MapPin,
  Eye,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export function ClientsPage() {
  const { t, isSpanish } = useLanguage();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    tax_id: '',
    has_tax_retention: false,
    payment_terms: 30,
    notes: '',
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchClients = useCallback(async () => {
    try {
      const response = await getClients();
      setClients(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await updateClient(editingClient._id, formData);
      } else {
        await createClient(formData);
      }
      toast.success(t('successSaved'));
      setIsDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      contact_name: client.contact_name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      tax_id: client.tax_id || '',
      has_tax_retention: Boolean(client.has_tax_retention),
      payment_terms: client.payment_terms || 30,
      notes: client.notes || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        await deleteClient(id);
        toast.success(t('successDeleted'));
        fetchClients();
      } catch (error) {
        toast.error(t('errorOccurred'));
      }
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({
      name: '',
      contact_name: '',
      email: '',
      phone: '',
      address: '',
      tax_id: '',
      has_tax_retention: false,
      payment_terms: 30,
      notes: '',
    });
  };

  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout title={t('clients')}>
      <div className="space-y-6" data-testid="clients-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <Input
              placeholder={t('search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 valhalla-input"
              data-testid="client-search"
            />
          </div>

          <AppDialog
            open={isDialogOpen}
            onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}
            size="sm"
            title={editingClient ? t('edit') : t('newClient')}
            trigger={
              <ActionButton
                icon={Plus}
                label={t('newClient')}
                tone="primary"
                className="font-semibold"
                data-testid="new-client-btn"
              />
            }
          >
              <ClientForm
                formData={formData}
                setFormData={setFormData}
                t={t}
                onSubmit={handleSubmit}
                onCancel={() => setIsDialogOpen(false)}
              />
          </AppDialog>
        </div>

        {/* Clients Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client) => (
            <div key={client._id} className="valhalla-card p-5" data-testid={`client-card-${client._id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#E2B05E] to-[#F0C47A] flex items-center justify-center text-[#0B0E14] font-bold text-lg">
                    {client.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#F3F4F6]">{client.name}</h3>
                    {client.contact_name && (
                      <p className="text-sm text-[#9CA3AF]">{client.contact_name}</p>
                    )}
                  </div>
                </div>
                <StatusBadge
                  value={client.is_active !== false ? 'activo' : 'inactivo'}
                  label={client.is_active !== false ? t('active') : t('inactive')}
                />
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-[#9CA3AF]">
                  <Envelope className="w-4 h-4" />
                  <span className="truncate">{client.email}</span>
                </div>
                {client.phone && (
                  <div className="flex items-center gap-2 text-[#9CA3AF]">
                    <Phone className="w-4 h-4" />
                    <span>{client.phone}</span>
                  </div>
                )}
                {client.address && (
                  <div className="flex items-center gap-2 text-[#9CA3AF]">
                    <MapPin className="w-4 h-4" />
                    <span className="truncate">{client.address}</span>
                  </div>
                )}
                {client.has_tax_retention && (
                  <Badge className="border-amber-500/30 bg-amber-500/20 text-amber-300">
                    Retención ITBMS
                  </Badge>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-[#242E42] flex items-center justify-between">
                <div className="text-sm text-[#9CA3AF]">
                  Payment: <span className="text-[#F3F4F6]">{client.payment_terms || 30} days</span>
                </div>
                <ActionGroup className="gap-1">
                  <ActionButton
                    icon={Eye}
                    tone="warning"
                    iconOnly
                    onClick={() => navigate(`/clients/${client._id}`)}
                    title={isSpanish ? 'Ver Dashboard 360' : 'View 360 Dashboard'}
                  />
                  <ActionButton icon={PencilSimple} tone="edit" iconOnly onClick={() => handleEdit(client)} />
                  <ActionButton icon={Trash} tone="delete" iconOnly onClick={() => handleDelete(client._id)} />
                </ActionGroup>
              </div>
            </div>
          ))}
        </div>

        {filteredClients.length === 0 && !loading && (
          <div className="valhalla-card p-12 text-center">
            <Buildings className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
            <p className="text-[#9CA3AF]">{t('noData')}</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ClientsPage;
